import React, { MutableRefObject, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { IBMLocale } from 'src/common/locale/mapValidLocale';
import { useSmallBreakpoint } from 'src/common/hooks/widgets/useBreakpoint';
import { useEnv } from 'src/common/hooks/widgets/useEnv';
import { useCookiesURL } from 'src/common/hooks/widgets/useCookiesURL';
import countryList from '../services/currencies.json';

import * as URX_FORM from '../constants/urx-form';
import {
  EST_MAX_API_CALLS,
  EST_MAX_HOD_API_CALLS,
  EST_MAX_SITES,
  EST_MIN_API_CALLS,
  EST_MIN_HOD_API_CALLS,
  EST_MIN_SITES,
  UT30EIS,
} from '../constants/spread';
import { Helmet } from 'react-helmet';
import {
  Button,
  ContentSwitcher,
  InlineNotification,
  NumberInput,
  RadioTile,
  SkeletonPlaceholder,
  Switch,
  Tag,
  TileGroup,
  Tooltip,
  Modal,
} from 'carbon-components-react';
import './EISPriceEstimator.scss';
import 'src/common/style/common.scss';
import { usePrice } from '../hooks/usePrice';
import Cookies from 'js-cookie';
import Footnote from './Footnote';
import messages from '../locales/messages';
import { FormattedMessage, useIntl } from 'react-intl';
import Thanks from './Thanks';
import { segmentsTracking } from 'src/common/lib/tracker';
import { initUrxForm } from 'src/common/hooks/widgets/urxForm';
import { swapCountryAndLanguage } from 'src/common/utils/swapCountryAndLanguage';
import { useBPName } from 'src/common/hooks/widgets/useBPName';
import BusinessPartner from 'src/common/components/BusinessPartner/BusinessPartner';
import { useNumberFormat } from 'src/common/hooks/widgets/useNumberFormat';
import {
  EISPackage,
  StandardTabInfo,
  Tiles,
  metaData,
} from '../constants/default';
import { FormModal } from 'src/common/utils/FormModal';
import { shortcode } from '../EISPriceEstimator.widget';
import {
  LangDefaults,
  determineCountry,
  getMessage,
  useLang,
} from 'src/common/hooks/widgets/useLang';
export interface EISPriceEstimatorProps {
  langCode: string;
  test: boolean;
  countryToTest: string;
}

const EISPriceEstimator = ({
  langCode,
  test,
  countryToTest,
}: EISPriceEstimatorProps) => {
  const localeCode = swapCountryAndLanguage(langCode as IBMLocale);
  const [urxlang] = useState(localeCode.toString());
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isSmall = useSmallBreakpoint('560px');
  const [environment] = useEnv();
  const [formComplete, setFormComplete] = useState(false);
  const [tileValueSelected, setTileValueSelected] = useState(Tiles.tile1);
  const [packageName, setPackageName] = useState(EISPackage.essentials);
  const [sitesAmount, setSitesAmount] = useState(5);
  const [apiCallsAmount, setApiCallsAmount] = useState(200000);
  const [hodApiCallsAmount, setHodApiCallsAmount] = useState(40000);
  const [switchIndex, setSwitchIndex] = useState(-1);
  const [configurationSize, setConfigurationSize] = useState(
    StandardTabInfo.tab1,
  );
  const [open, setOpen] = useState(false);
  const [formattedAPICalls] = useNumberFormat(apiCallsAmount);
  const [formattedHODAPICalls] = useNumberFormat(hodApiCallsAmount);
  const sitesRef = useRef() as MutableRefObject<HTMLInputElement>;
  const apiCallsRef = useRef() as MutableRefObject<HTMLInputElement>;
  const hodApiCallsRef = useRef() as MutableRefObject<HTMLInputElement>;
  const cookieURL = useCookiesURL();
  const [currency, setCurrency] = useState('');
  const [country, setCountry] = useState('');
  const [totalAppPoint, setTotalAppPoint] = useState(0);

  const [totalCost, totalCostMonthly, loading] = usePrice({
    currency,
    localeCode,
    sitesAmount,
    apiCallsAmount,
    hodApiCallsAmount,
    tileValueSelected,
    country,
    setTotalAppPoint,
  });
  const [lang, userLocale] = useLang(
    countryList,
    test,
    countryToTest,
    setCurrency,
    setCountry,
    localeCode,
  );

  const { bpName, logo, companyId, desc } = useBPName(UT30EIS, true);

  const [localCurrency, setlocalCurrency] = useState<string>(localeCode);

  const [toggleCountry, setToggleCountry] = useState(false);
  const [modalLocalCountry, setModalLocalCountry] = useState('');
  const [modalLocalCurrency, setModalLocalCurrency] = useState('USD');
  const [modalLocalLanguage, setModalLocalLanguage] = useState('English');

  const [localExist, setLocalExist] = useState(false);
  let localStorageFound = localStorage.getItem('langLocale');
  if (localStorageFound === null)
    localStorageFound = LangDefaults.localCountryLang;
  const [selectedLocale, setSelectedLocale] = useState(localStorageFound);

  // Determine Country
  determineCountry(
    setLocalExist,
    setSelectedLocale,
    setModalLocalCountry,
    setModalLocalLanguage,
    setModalLocalCurrency,
    setToggleCountry,
    lang,
    countryList,
    true,
    test,
    localeCode,
  );

  useEffect(() => {
    setlocalCurrency(userLocale); // show local Currency
  }, [userLocale, localeCode, localCurrency]);

  useEffect(() => {
    // On pre-prod and prod widget will have the the parent div with shortcode classname.
    // So we are applying margin because some AEM classname causing style issue.
    const shortCodeStyle = Array.from(
      document.getElementsByClassName(
        shortcode,
      ) as HTMLCollectionOf<HTMLElement>,
    )[0];
    // In local shortcode will be alway undefined, to avoid app crash on local we are
    // checking the below condition and it will only work in pre-prod and prod version.
    if (shortCodeStyle !== undefined) {
      shortCodeStyle.style.marginLeft = '1rem';
    }
  }, []);

  const invalidSites =
    parseInt(sitesRef?.current?.value) < EST_MIN_SITES ||
    parseInt(sitesRef?.current?.value) > EST_MAX_SITES ||
    sitesRef?.current?.value === '';
  const invalidApiCalls =
    parseInt(apiCallsRef?.current?.value) < EST_MIN_API_CALLS ||
    parseInt(apiCallsRef?.current?.value) > EST_MAX_API_CALLS ||
    apiCallsRef?.current?.value === '';

  const invalidHodApiCalls =
    parseInt(hodApiCallsRef?.current?.value) < EST_MIN_HOD_API_CALLS ||
    parseInt(hodApiCallsRef?.current?.value) > EST_MAX_HOD_API_CALLS ||
    hodApiCallsRef?.current?.value === '';

  window.urxEnvironment = environment;
  window.onUrxFormSubmitSuccess = () => {
    setFormComplete(!formComplete);
    setDrawerOpen(false); // Hide Drawer on Thank you page
  };

  useEffect(() => {
    if (tileValueSelected === Tiles.tile1) {
      setSitesAmount(EST_MIN_SITES);
      setApiCallsAmount(EST_MIN_API_CALLS);
      setHodApiCallsAmount(EST_MIN_HOD_API_CALLS);
      setSwitchIndex(-1);
    } else if (tileValueSelected === Tiles.tile3) {
      setSwitchIndex(-1);
    }
  }, [tileValueSelected]);

  useEffect(() => {
    setCookie();
  }, [
    sitesAmount,
    apiCallsAmount,
    hodApiCallsAmount,
    totalCostMonthly,
    totalCost,
    packageName,
  ]);

  const setCookie = () => {
    Cookies.set(
      'urxdcq',
      JSON.stringify({
        source: URX_FORM.FORM_ID,
        // One field to provide all values (stringify)
        dcq: {
          Q_NOTESDATA:
            cookieURL +
            ' Number of Sites: ' +
            sitesAmount +
            ' | Number of Weather API Calls: ' +
            formattedAPICalls +
            ' | Number of History on Demand (HoD) API Calls: ' +
            formattedHODAPICalls +
            ' | Package: ' +
            packageName +
            ' | AppPoints: ' +
            totalAppPoint +
            ' | Estimated Monthly cost: ' +
            totalCostMonthly +
            ' | Estimated Annual cost: ' +
            totalCost,
        },
      }),
    );
  };

  const intl = useIntl();
  const essentialsFeature = [
    intl.formatMessage(messages.est_feat_pt1),
    intl.formatMessage(messages.est_feat_pt2),
    intl.formatMessage(messages.est_feat_pt3),
    intl.formatMessage(messages.est_feat_pt4),
    intl.formatMessage(messages.est_feat_pt5),
    intl.formatMessage(messages.est_feat_pt6),
  ];

  const stdVegetationFeature = [
    intl.formatMessage(messages.switch1_tag_1),
    intl.formatMessage(messages.switch1_tag_2, { name: 'Geiger-mode LiDAR' }),
    intl.formatMessage(messages.switch1_tag_3),
    intl.formatMessage(messages.switch1_tag_4),
    intl.formatMessage(messages.switch1_tag_5),
    intl.formatMessage(messages.switch1_tag_6),
  ];

  const stdOutageFeature = [
    intl.formatMessage(messages.switch2_tag_1),
    intl.formatMessage(messages.switch2_tag_2),
    intl.formatMessage(messages.switch2_tag_3),
    intl.formatMessage(messages.switch2_tag_4),
    intl.formatMessage(messages.switch2_tag_5),
  ];

  const stdCropYieldFeature = [
    intl.formatMessage(messages.switch3_tag_1),
    intl.formatMessage(messages.switch3_tag_2),
    intl.formatMessage(messages.switch3_tag_4),
    intl.formatMessage(messages.switch3_tag_5),
    intl.formatMessage(messages.switch3_tag_6),
  ];

  const premRenewableFeature = [
    intl.formatMessage(messages.prem_tag_1),
    intl.formatMessage(messages.prem_tag_2),
    intl.formatMessage(messages.prem_tag_3),
    intl.formatMessage(messages.prem_tag_4),
    intl.formatMessage(messages.prem_tag_5),
    intl.formatMessage(messages.prem_tag_6),
    intl.formatMessage(messages.prem_tag_7),
    intl.formatMessage(messages.prem_tag_8),
    intl.formatMessage(messages.prem_tag_9),
  ];

  const premNotificationLabel = getMessage(messages.prem_notif_label);

  useEffect(() => {
    switch (switchIndex) {
      case 0: {
        setConfigurationSize(StandardTabInfo.tab1);
        break;
      }
      case 1: {
        setConfigurationSize(StandardTabInfo.tab2);
        break;
      }
      case 2: {
        setConfigurationSize(StandardTabInfo.tab3);
        break;
      }
    }
  }, [switchIndex, configurationSize, tileValueSelected]);

  const recommendText = getMessage(messages.recommend_text_std);

  const handleModal1 = () => {
    setOpen(true);
    segmentsTracking('CTA Clicked', {
      pageTitle: 'Environmental Intelligence Suite Pricing Page',
      object: 'Pricing page',
      resultValue: 'CTA clicked',
      CTA: 'Modal open ' + modalLocalCountry,
      location: 'Estimator',
      action: '',
      field: '',
    });
  };

  const handleTakeMe = () => {
    if (tileValueSelected === Tiles.tile1) {
      setTileValueSelected('standard');
    } else if (tileValueSelected === Tiles.tile2) {
      setTileValueSelected('premium');
    }
  };

  type ModalStateManagerProps = {
    renderLauncher: any;
    children: React.ComponentType<LauncherContentProps>;
  };

  type LauncherContentProps = {
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  };

  const ModalStateManager: React.FC<ModalStateManagerProps> = ({
    renderLauncher: LauncherContent,
    children: ModalContent,
  }) => {
    return (
      <>
        {!ModalContent || typeof document === 'undefined'
          ? null
          : ReactDOM.createPortal(
              <ModalContent open={open} setOpen={setOpen} />,
              document.body,
            )}
        {LauncherContent && <LauncherContent open={open} setOpen={setOpen} />}
      </>
    );
  };

  return (
    <div className="eis_estimator widget-styles">
      <Helmet>
        <script
          type="application/javascript"
          src={`https://www.ibm.com/account/ibmidutil/widget/js/loader.js`}
        ></script>
        <script
          type="application/javascript"
          src={`https://www.ibm.com/account/ibmidutil/widget/js/main.js`}
        ></script>
      </Helmet>
      {formComplete ? (
        <Thanks
          sitesAmount={sitesAmount}
          apiCallsAmount={formattedAPICalls}
          hodApiCallsAmount={formattedHODAPICalls}
          licensePriceMonthly={`${totalCostMonthly}`}
          packageName={packageName}
          bpName={bpName}
        />
      ) : (
        <div id="EISGrid">
          {bpName ? (
            <BusinessPartner bpName={bpName} logo={logo} desc={desc} />
          ) : null}

          <div className="bx--row modal-mt">
            <div
              className="bx--col-lg-4 bx--col-md-8 bx--col-sm-8 bx--offset-lg-12"
              id="countryModal"
            >
              {/* For now globalization is disabled */}
              {localExist && toggleCountry ? (
                <ModalStateManager
                  renderLauncher={() => (
                    <Button onClick={handleModal1}>
                      Access Page in
                      {' ' + modalLocalLanguage}
                    </Button>
                  )}
                >
                  {({ open, setOpen }) => (
                    <Modal
                      open={open}
                      onRequestClose={() => setOpen(false)}
                      modalLabel={`Found country ${modalLocalCountry}. Access page in ${modalLocalLanguage} (${modalLocalCurrency})`}
                      modalHeading={`The Environmental Intelligence Suite Price estimator is available in ${modalLocalLanguage} with local currency ${modalLocalCurrency}`}
                      size={'sm'}
                      primaryButtonText={'Cool! Take me there'}
                      secondaryButtonText={'Stay here'}
                      onRequestSubmit={() => {
                        localStorage.setItem('langLocale', selectedLocale);
                        (window.location.href =
                          'https://www.ibm.com/' +
                          selectedLocale +
                          '/products/envizi/pricing'),
                          segmentsTracking('CTA Clicked', {
                            pageTitle:
                              'Environmental Intelligence Suite Pricing Page',
                            object: 'Pricing page',
                            resultValue: 'CTA clicked',
                            CTA: 'Switched to country ' + modalLocalCountry,
                            location: 'Estimator',
                            action: '',
                            field: '',
                          });
                        return true;
                      }}
                      onSecondarySubmit={() => {
                        segmentsTracking('CTA Clicked', {
                          pageTitle:
                            'Environmental Intelligence Suite Pricing Page',
                          object: 'Pricing page',
                          resultValue: 'CTA clicked',
                          CTA: 'Stay here button clicked ' + modalLocalCountry,
                          location: 'Estimator',
                          action: '',
                          field: '',
                        });
                        setOpen(false);
                        return true;
                      }}
                    ></Modal>
                  )}
                </ModalStateManager>
              ) : null}
            </div>
          </div>
          <div className="bx--row mar3">
            <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-4">
              <div className="page_sub_heading marginleft_1">
                <FormattedMessage {...messages.eis_headline} />
              </div>
              <p className="page_body padding_24_3 mar2 marginleft_1">
                <FormattedMessage {...messages.eis_sub_headline} />
              </p>
              <div className="bx--row">
                <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-4">
                  <TileGroup
                    className="eis_estimator__tilegroup"
                    name="radio tile group"
                    valueSelected={tileValueSelected}
                  >
                    <RadioTile
                      className="eis_estimator__tile"
                      id="radio-tile-1"
                      value="essentials"
                      onClick={() => {
                        setTileValueSelected(Tiles.tile1);
                        setPackageName(EISPackage.essentials);
                        segmentsTracking('CTA Clicked', {
                          pageTitle:
                            'Environmental Intelligence Suite Pricing Page',
                          object: 'Pricing page',
                          resultValue: 'CTA clicked',
                          CTA: 'Essentials Tile',
                          location: 'Estimator',
                          action: '',
                          field: '',
                        });
                      }}
                    >
                      <div className="page_sub_heading tile-label">
                        {EISPackage.essentials}
                      </div>
                      <div className="page_body_2">
                        <span>
                          <ul className="list-item">
                            <li>
                              <p className="page_body_2">
                                <FormattedMessage {...messages.est_bullet_1} />
                              </p>
                            </li>
                            <li>
                              <p className="page_body_2">
                                <FormattedMessage {...messages.est_bullet_2} />
                              </p>
                            </li>
                          </ul>
                        </span>
                      </div>
                    </RadioTile>
                    <RadioTile
                      id="radio-tile-2"
                      value="standard"
                      className="eis_estimator__tile hide1"
                      onClick={() => {
                        setTileValueSelected(Tiles.tile2);
                        setPackageName(EISPackage.standard);
                        segmentsTracking('CTA Clicked', {
                          pageTitle:
                            'Environmental Intelligence Suite Pricing Page',
                          object: 'Pricing page',
                          resultValue: 'CTA clicked',
                          CTA: 'Standard Tile',
                          location: 'Estimator',
                          action: '',
                          field: '',
                        });
                      }}
                    >
                      <div className="page_sub_heading tile-label">
                        {EISPackage.standard}
                      </div>
                      <div className="page_body_2">
                        <span>
                          <ul className="list-item">
                            <li>
                              <p className="page_body_2">
                                <FormattedMessage {...messages.std_bullet_1} />
                              </p>
                            </li>
                            <li>
                              <p className="page_body_2">
                                <FormattedMessage {...messages.std_bullet_2} />
                              </p>
                            </li>
                            <li>
                              <p className="page_body_2">
                                <FormattedMessage {...messages.std_bullet_3} />
                              </p>
                            </li>
                          </ul>
                        </span>
                      </div>
                    </RadioTile>
                    <RadioTile
                      id="radio-tile-3"
                      value="premium"
                      className="hide1"
                      onClick={() => {
                        setTileValueSelected(Tiles.tile3);
                        setPackageName(EISPackage.premium);
                        segmentsTracking('CTA Clicked', {
                          pageTitle:
                            'Environmental Intelligence Suite Pricing Page',
                          object: 'Pricing page',
                          resultValue: 'CTA clicked',
                          CTA: 'Premium Tile',
                          location: 'Estimator',
                          action: '',
                          field: '',
                        });
                      }}
                    >
                      <div className="page_sub_heading tile-label">
                        {EISPackage.premium}
                      </div>
                      <div className="page_body_2">
                        <span>
                          <ul className="list-item">
                            <li>
                              <p className="page_body_2">
                                <FormattedMessage {...messages.prem_bullet_1} />
                              </p>
                            </li>
                            <li>
                              <p className="page_body_2">
                                <FormattedMessage {...messages.prem_bullet_2} />
                              </p>
                            </li>
                            <li>
                              <p className="page_body_2">
                                <FormattedMessage {...messages.prem_bullet_3} />
                              </p>
                            </li>
                          </ul>
                        </span>
                      </div>
                    </RadioTile>
                  </TileGroup>
                </div>
              </div>
              <div className="bx--row">
                <div className="bx--col-lg-10 bx--col-md-16 bx--col-sm-16 mar2">
                  <div>
                    {tileValueSelected === Tiles.tile1 ? (
                      <>
                        <div className="page_body_2 mar4 marginleft_1">
                          <FormattedMessage {...messages.est_feat_label} />
                        </div>
                        <div className="marginleft_1">
                          {essentialsFeature.map(
                            (item: string, index: number) => (
                              <Tag
                                key={index}
                                type={'purple'}
                                size="md"
                                disabled={false}
                                filter={false}
                                className="tag-margin"
                              >
                                {item}
                              </Tag>
                            ),
                          )}
                        </div>
                      </>
                    ) : tileValueSelected === Tiles.tile2 ? (
                      <>
                        <div className="page_body_2 mar4 marginleft_1">
                          <FormattedMessage
                            {...messages.std_desc_part1}
                            values={{
                              name1: metaData.text3,
                              name2: metaData.text3,
                            }}
                          />
                          <br />
                          <br />
                        </div>
                        <div className="page_body_2 marginleft_1">
                          <FormattedMessage
                            {...messages.std_desc_part2}
                            values={{
                              name1: metaData.text4,
                              name2: metaData.text5,
                            }}
                          />
                          <br />
                          <br />
                        </div>
                        <div className="mar4 marginleft_1">
                          <span className="page_sub_heading_2">
                            <FormattedMessage {...messages.std_tab_info} />
                            <br />
                            <br />
                          </span>
                          <ContentSwitcher
                            className="eis_estimator__switcher-config mar8"
                            selectionMode="manual"
                            selectedIndex={switchIndex}
                            size="lg"
                            onChange={(obj) => {
                              const { index, name } = obj;
                              setSwitchIndex(index == undefined ? 0 : index);
                              segmentsTracking('CTA Clicked', {
                                pageTitle:
                                  'Environmental Intelligence Suite Pricing Page',
                                object: 'Pricing page',
                                resultValue: `CTA clicked ${name}`,
                                CTA: `switcher`,
                                location: 'Estimator',
                                action: '',
                                field: '',
                              });
                            }}
                          >
                            <Switch
                              name={StandardTabInfo.tab1}
                              text={StandardTabInfo.tab1}
                            />
                            <Switch
                              name={StandardTabInfo.tab2}
                              text={StandardTabInfo.tab2}
                            />
                            <Switch
                              name={StandardTabInfo.tab3}
                              text={StandardTabInfo.tab3}
                            />
                          </ContentSwitcher>
                        </div>
                        {switchIndex === 0 && (
                          <>
                            <div className="page_body_2 mar4 marginleft_1">
                              <FormattedMessage
                                {...messages.switch1_desc}
                                values={{ name: metaData.text9 }}
                              />
                            </div>
                            <div className="mar4 mar1 marginleft_1">
                              {stdVegetationFeature.map(
                                (item: string, index: number) => (
                                  <Tag
                                    key={index}
                                    type={'purple'}
                                    size="md"
                                    disabled={false}
                                    filter={false}
                                    className="tag-margin"
                                  >
                                    {item}
                                  </Tag>
                                ),
                              )}
                            </div>
                          </>
                        )}
                        {switchIndex === 1 && (
                          <>
                            <div className="page_body_2 mar4 marginleft_1">
                              <FormattedMessage {...messages.switch2_desc} />
                            </div>
                            <div className="mar3 mar1 marginleft_1">
                              {stdOutageFeature.map(
                                (item: string, index: number) => (
                                  <Tag
                                    key={index}
                                    type={'purple'}
                                    size="md"
                                    disabled={false}
                                    filter={false}
                                    className="tag-margin"
                                  >
                                    {item}
                                  </Tag>
                                ),
                              )}
                            </div>
                          </>
                        )}
                        {switchIndex === 2 && (
                          <>
                            <div className="page_body_2 mar4 marginleft_1">
                              <FormattedMessage
                                {...messages.switch3_desc}
                                values={{
                                  name: metaData.text6,
                                }}
                              />
                            </div>
                            <div className="mar3 mar1 marginleft_1">
                              {stdCropYieldFeature.map(
                                (item: string, index: number) => (
                                  <Tag
                                    key={index}
                                    type={'purple'}
                                    size="md"
                                    disabled={false}
                                    filter={false}
                                    className="tag-margin"
                                  >
                                    {item}
                                  </Tag>
                                ),
                              )}
                            </div>
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="page_body_2 mar3 marginleft_1">
                          <FormattedMessage
                            {...messages.prem_short_desc}
                            values={{
                              name: metaData.text8,
                            }}
                          />
                        </div>
                        <div className="mar3 mar1 marginleft_1">
                          {premRenewableFeature.map(
                            (item: string, index: number) => (
                              <Tag
                                key={index}
                                type={'purple'}
                                size="md"
                                disabled={false}
                                filter={false}
                                className="tag-margin"
                              >
                                {item}
                              </Tag>
                            ),
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  {tileValueSelected === Tiles.tile1 && (
                    <>
                      <div className="bx--row mar9 mar1">
                        <div className="bx--col-lg-8 bx--col-md-16 bx--col-sm-16 marginleft_1">
                          <br />
                          <Tooltip
                            onChange={() => {
                              segmentsTracking('CTA Clicked', {
                                pageTitle:
                                  'Environmental Intelligence Suite Pricing Page',
                                object: 'Pricing page',
                                resultValue: 'CTA clicked',
                                CTA: 'Sites tooltip',
                                location: 'Estimator',
                                action: '',
                                field: '',
                              });
                            }}
                            direction={isSmall ? 'bottom' : 'right'}
                            tabIndex={0}
                            triggerText={
                              <span className="page_body">
                                <FormattedMessage {...messages.slider1_text} />
                              </span>
                            }
                          >
                            <span className="slide_text">
                              <FormattedMessage
                                {...messages.tooltip_sites_text}
                              />
                            </span>
                          </Tooltip>
                          <NumberInput
                            id="sites"
                            size="md"
                            allowEmpty={true}
                            ref={sitesRef}
                            invalid={false}
                            invalidText={
                              tileValueSelected === Tiles.tile1 && (
                                <FormattedMessage
                                  {...messages.sites_invalid_text}
                                />
                              )
                            }
                            className="number-input"
                            helperText={
                              tileValueSelected === Tiles.tile1 && (
                                <div>
                                  <p className="helper-text">
                                    <FormattedMessage
                                      {...messages.est_sites_info1}
                                    />
                                  </p>
                                  <p className="helper-text">
                                    <FormattedMessage
                                      {...messages.est_sites_info2}
                                    />
                                  </p>
                                </div>
                              )
                            }
                            min={EST_MIN_SITES}
                            max={EST_MAX_SITES}
                            value={sitesAmount}
                            onChange={() => {
                              segmentsTracking('CTA Clicked', {
                                pageTitle:
                                  'Environmental Intelligence Suite Pricing Page',
                                object: 'Pricing page',
                                resultValue: 'CTA clicked',
                                CTA: 'Total Sites',
                                location: 'Estimator',
                                action: '',
                                field: '',
                              });
                              setSitesAmount(Number(sitesRef.current.value));
                            }}
                          />
                          <br />
                        </div>
                        <div className="bx--col-lg-7 bx--col-md-16 bx--col-sm-16 marginleft_1">
                          <br />
                          <Tooltip
                            onChange={() => {
                              segmentsTracking('CTA Clicked', {
                                pageTitle:
                                  'Environmental Intelligence Suite Pricing Page',
                                object: 'Pricing page',
                                resultValue: 'CTA clicked',
                                CTA: 'Weather API Calls tooltip',
                                location: 'Estimator',
                                action: '',
                                field: '',
                              });
                            }}
                            direction={isSmall ? 'bottom' : 'right'}
                            tabIndex={0}
                            triggerText={
                              <span className="page_body">
                                <FormattedMessage
                                  {...messages.slider2_text}
                                  values={{ name: metaData.text1 }}
                                />
                              </span>
                            }
                          >
                            <span className="slide_text">
                              <FormattedMessage
                                {...messages.tooltip_api_text}
                              />
                            </span>
                          </Tooltip>
                          <NumberInput
                            id="apiCalls"
                            size="md"
                            allowEmpty={true}
                            ref={apiCallsRef}
                            step={20000}
                            invalidText={
                              tileValueSelected === Tiles.tile1 && (
                                <FormattedMessage
                                  {...messages.api_calls_invalid_text}
                                />
                              )
                            }
                            helperText={
                              tileValueSelected === Tiles.tile1 && (
                                <div>
                                  <p className="helper-text">
                                    <FormattedMessage
                                      {...messages.est_api_info1}
                                    />
                                  </p>
                                  <p className="helper-text">
                                    <FormattedMessage
                                      {...messages.est_api_info2}
                                    />
                                  </p>
                                </div>
                              )
                            }
                            min={EST_MIN_API_CALLS}
                            max={EST_MAX_API_CALLS}
                            value={apiCallsAmount}
                            onChange={() => {
                              segmentsTracking('CTA Clicked', {
                                pageTitle:
                                  'Environmental Intelligence Suite Pricing Page',
                                object: 'Pricing page',
                                resultValue: 'CTA clicked',
                                CTA: 'Total Weather API Calls',
                                location: 'Estimator',
                                action: '',
                                field: '',
                              });
                              setApiCallsAmount(
                                Number(apiCallsRef.current.value),
                              );
                            }}
                          />
                        </div>
                      </div>
                      <div className="bx--row mar9 mar1">
                        <div className="bx--col-lg-8 bx--col-md-16 bx--col-sm-16 marginleft_1">
                          <Tooltip
                            onChange={() => {
                              segmentsTracking('CTA Clicked', {
                                pageTitle:
                                  'Environmental Intelligence Suite Pricing Page',
                                object: 'Pricing page',
                                resultValue: 'CTA clicked',
                                CTA: 'History on Demand (HoD) API Calls tooltip',
                                location: 'Estimator',
                                action: '',
                                field: '',
                              });
                            }}
                            direction={isSmall ? 'bottom' : 'right'}
                            tabIndex={0}
                            triggerText={
                              <span className="page_body">
                                <FormattedMessage
                                  {...messages.slider3_text}
                                  values={{ name: metaData.text2 }}
                                />
                              </span>
                            }
                          >
                            <span className="slide_text">
                              <FormattedMessage
                                {...messages.tooltip_hod_text}
                              />
                            </span>
                          </Tooltip>
                          <NumberInput
                            id="hodApiCalls"
                            size="md"
                            allowEmpty={true}
                            ref={hodApiCallsRef}
                            step={10000}
                            invalidText={
                              tileValueSelected === Tiles.tile1 && (
                                <FormattedMessage
                                  {...messages.hod_api_calls_invalid_text}
                                />
                              )
                            }
                            helperText={
                              tileValueSelected === Tiles.tile1 && (
                                <div>
                                  <p className="helper-text">
                                    <FormattedMessage
                                      {...messages.est_hod_info1}
                                    />
                                  </p>
                                  <p className="helper-text">
                                    <FormattedMessage
                                      {...messages.est_hod_info2}
                                    />
                                  </p>
                                </div>
                              )
                            }
                            min={EST_MIN_HOD_API_CALLS}
                            max={EST_MAX_HOD_API_CALLS}
                            value={hodApiCallsAmount}
                            onChange={() => {
                              segmentsTracking('CTA Clicked', {
                                pageTitle:
                                  'Environmental Intelligence Suite Pricing Page',
                                object: 'Pricing page',
                                resultValue: 'CTA clicked',
                                CTA: 'Total History on Demand (HoD) API Calls',
                                location: 'Estimator',
                                action: '',
                                field: '',
                              });
                              setHodApiCallsAmount(
                                Number(hodApiCallsRef.current.value),
                              );
                            }}
                          />
                        </div>
                      </div>
                    </>
                  )}
                  {tileValueSelected === Tiles.tile1 &&
                    (sitesAmount > EST_MAX_SITES ||
                      apiCallsAmount > EST_MAX_API_CALLS ||
                      hodApiCallsAmount > EST_MAX_HOD_API_CALLS) && (
                      <div className="bx--row">
                        <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                          <InlineNotification
                            title={recommendText}
                            kind={'info-square'}
                            actions={
                              <p
                                className="notification-text"
                                onClick={handleTakeMe}
                              >
                                <FormattedMessage {...messages.std_notif_txt} />
                              </p>
                            }
                          />
                        </div>
                      </div>
                    )}
                  {tileValueSelected === Tiles.tile1 && (
                    <div id="footnote" className="div-only-lg">
                      <div className="bx--row">
                        <div className="bx--col-lg-16 marginleft_1">
                          <div>
                            <Footnote numNote={1} />
                          </div>
                          <div className="footnote_text">
                            <FormattedMessage {...messages.disclaimer_text} />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="bx--col-lg-6 bx--col-md-16 bx--col-sm-16">
                  {tileValueSelected === Tiles.tile1 ? (
                    <Tag type="outline" className="tag-margin1">
                      {EISPackage.essentials}
                    </Tag>
                  ) : tileValueSelected === Tiles.tile2 ? (
                    <Tag type="outline" className="tag-margin1">
                      {EISPackage.standard}
                    </Tag>
                  ) : (
                    <Tag type="outline" className="tag-margin1">
                      {EISPackage.premium}
                    </Tag>
                  )}
                  {tileValueSelected === Tiles.tile3 ||
                  tileValueSelected === Tiles.tile2 ? (
                    <div className={`${!isSmall ? 'tag-margin2' : ''}`}>
                      <InlineNotification
                        kind="info"
                        role="onprem"
                        title={premNotificationLabel}
                        subtitle={
                          <FormattedMessage {...messages.contact_info} />
                        }
                        statusIconDescription="On-Prem Info"
                        hideCloseButton={true}
                      />
                      <Button
                        id="request_btn"
                        target="_self"
                        data-ibm-contact="scheduler-link"
                        className="eis_estimator__stack-button btn-set mar4"
                        href="#"
                        onClick={() => {
                          segmentsTracking('CTA Clicked', {
                            pageTitle:
                              'Environmental Intelligence Suite Pricing Page',
                            object: 'Pricing page',
                            resultValue: 'CTA clicked',
                            CTA: 'Book a Consultation',
                            location: 'Estimator',
                            action: '',
                            field: '',
                          });
                          window.open(
                            ' https://www.ibm.com/products/environmental-intelligence-suite/pricing/?schedulerform=',
                            '_self',
                          );
                        }}
                      >
                        <FormattedMessage {...messages.book_text} />
                      </Button>
                    </div>
                  ) : (
                    <div id="stickyNav" className="bx--row">
                      <div className="sticky-nav price_table_comp bg_white">
                        <table className="cds--data-table cds--data-table--sm">
                          <thead>
                            <tr>
                              <th scope="col">
                                <div className="price_table_comp__heading">
                                  <FormattedMessage {...messages.col1_text} />
                                </div>
                              </th>
                              <th scope="col">
                                <div className="cds--table-header-label">
                                  <div className="price_table_comp__heading">
                                    <FormattedMessage {...messages.col2_text} />
                                  </div>
                                </div>
                              </th>
                            </tr>
                          </thead>
                          <tbody aria-live="polite">
                            <tr>
                              <td>
                                {' '}
                                <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                  <FormattedMessage {...messages.sites_text} />
                                </div>
                              </td>
                              <td>
                                <div className="price_table_comp__quantity padding_8">{`${sitesAmount}`}</div>
                              </td>
                            </tr>
                            <tr>
                              <td>
                                <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                  <FormattedMessage
                                    {...messages.api_calls_text}
                                  />
                                </div>
                              </td>
                              <td>
                                <div className="price_table_comp__quantity padding_8">
                                  {`${formattedAPICalls}`}{' '}
                                </div>
                              </td>
                            </tr>
                            <tr>
                              <td>
                                <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                  <FormattedMessage
                                    {...messages.hod_api_calls_text}
                                  />
                                </div>
                              </td>
                              <td>
                                <div className="price_table_comp__quantity padding_8">
                                  {`${formattedHODAPICalls}`}{' '}
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                        <div id="price_toggle_one">
                          {!invalidSites &&
                            !invalidApiCalls &&
                            !invalidHodApiCalls && (
                              <>
                                <p
                                  id="widget.estimated-cost"
                                  className="eis_estimator__ibm-type-e estmt-cost price_table_comp__bodycopy padding_24_2"
                                >
                                  <FormattedMessage
                                    {...messages.estimated_text}
                                  />
                                  <sup className="eis_estimator__sup">1</sup>
                                </p>

                                <div className="padding_8 price_table_comp__price">
                                  {loading ? (
                                    <SkeletonPlaceholder className="eis_estimator__sticky-nav-skeleton" />
                                  ) : (
                                    `${totalCostMonthly} *`
                                  )}
                                </div>
                                <p className="price_table_comp__disclaimer price_table_comp_color padding_24_1">
                                  *{' '}
                                  <FormattedMessage {...messages.prcie_text} />
                                </p>

                                <p
                                  id="widget.estimated-cost"
                                  className="eis_estimator__ibm-type-e estmt-cost price_table_comp__bodycopy"
                                >
                                  AppPoints required for this configuration
                                </p>
                                <div className="padding_8 price_table_comp__price">
                                  {totalAppPoint}
                                </div>

                                <div className="multi-btn">
                                  <Button
                                    id="request_btn"
                                    className="eis_estimator__stack-button mar10"
                                    onClick={() => {
                                      segmentsTracking('CTA Clicked', {
                                        pageTitle:
                                          'Environmental Intelligence Suite Pricing Page',
                                        object: 'Pricing page',
                                        resultValue: 'CTA clicked',
                                        CTA: 'Request a quote now',
                                        location: 'Estimator',
                                        action: '',
                                        field: '',
                                      });
                                      initUrxForm(
                                        environment,
                                        URX_FORM.INSTANCE_ID,
                                        URX_FORM.FORM_ID,
                                        urxlang as IBMLocale,
                                        companyId,
                                      );
                                      setDrawerOpen(true);
                                      setCookie();
                                    }}
                                  >
                                    <FormattedMessage
                                      {...messages.quote_text}
                                    />
                                  </Button>
                                  {tileValueSelected === 'essentials' &&
                                    sitesAmount === EST_MIN_SITES &&
                                    hodApiCallsAmount ===
                                      EST_MIN_HOD_API_CALLS &&
                                    apiCallsAmount === EST_MIN_API_CALLS && (
                                      <Button
                                        id="buynow_btn"
                                        kind="secondary"
                                        className="eis_estimator__stack-button"
                                        onClick={() => {
                                          segmentsTracking('CTA Clicked', {
                                            pageTitle:
                                              'Environmental Intelligence Suite Pricing Page',
                                            object: 'Pricing page',
                                            resultValue: 'CTA clicked',
                                            CTA: 'Buy Now',
                                            location: 'Estimator',
                                            action: '',
                                            field: '',
                                          });
                                          window.open(
                                            'https://www.ibm.com/marketplace/purchase/configuration/en/us/checkout?editionID=EIDTTPYM',
                                          );
                                        }}
                                      >
                                        Buy Now
                                      </Button>
                                    )}
                                </div>
                              </>
                            )}
                        </div>
                      </div>
                    </div>
                  )}
                  {tileValueSelected === Tiles.tile1 && (
                    <div id="footnote" className="div-only-media">
                      <div className="bx--row mar9">
                        <div className="bx--col-lg-9 marginleft_1">
                          <div>
                            <Footnote numNote={1} />
                          </div>
                          <div className="footnote_text">
                            <FormattedMessage {...messages.disclaimer_text} />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <FormModal
            pageTitle="Environmental Intelligence Suite Pricing Page"
            object="Environmental Intelligence Suite Pricing Page"
            productTitle={'Environmental Intelligence Suite'}
            pageUrl={''}
            location={'Estimator'}
            bpName={bpName}
            logo={logo}
            drawerOpen={drawerOpen}
            setDrawerOpen={setDrawerOpen}
            formId={URX_FORM.FORM_ID}
            lang={lang}
          ></FormModal>
        </div>
      )}
    </div>
  );
};

export default EISPriceEstimator;
